import {
  classNamesFunction,
  IButtonStyles,
  IComboBoxOptionStyles,
  IComboBoxStyles,
  IDropdownStyles,
  IStyle,
  ITextFieldStyles,
  ICheckboxStyles,
  ITheme,
  ISpinnerStyles,
} from '@fluentui/react';
import { isGetHelpSourceApp } from '../../../../web/utils/statelessUtil';
import { SourceApps } from '../../../models/constants/constants';
import { ISubwayNavStyles } from '../controls/SubwayNav';
import { RequestSeverity } from '../models/requestModels';
import { IThemedProp, color } from './themesUI';

// Request Details
export interface IRequestDetailsStyles {
  root: IStyle;
  sectionContainer: IStyle;
  contentContainer: IStyle;
  propertyLine: IStyle;
  propertyLabel: IStyle;
  propertyStateBullet: IStyle;
  propertyContentPrimary: IStyle;
  propertySeverityTitle: IStyle;
  field: IStyle;
}

export interface IRequestEditProps {
  sourceApp: string;
  severity: RequestSeverity;
  isDarkTheme?: boolean;
  isNonAgave?: boolean;
  isTeams?: boolean;
}

export interface ITextFieldStylesProps {
  isInputValid?: boolean;
  isPhoneOrEmailField?: boolean;
  isTeams?: boolean;
}

export type IRequestDetailsThemedProps = IRequestEditProps & IThemedProp;

export const getRequestDetailsStyles = (props: IRequestDetailsThemedProps): IRequestDetailsStyles => {
  const { theme, severity } = props;
  return {
    root: {
      marginTop: '15px',
      color: theme.palette.neutralPrimary,
      maxWidth: '768px',
    },
    sectionContainer: {
      margin: '32px 0',
      fontSize: '14px',
    },
    contentContainer: {
      margin: '16px 0',
    },
    propertyLine: {
      margin: '16px 0',
      display: 'flex',
    },
    propertyLabel: {
      flexBasis: '150px',
    },
    propertyStateBullet: {
      marginLeft: '5px',
      marginRight: '5px',
    },
    propertyContentPrimary: {
      marginRight: '5px',
    },
    propertySeverityTitle: {
      color:
        severity === RequestSeverity.SevA || severity === RequestSeverity.Critical || severity === RequestSeverity.Sev1
          ? theme.palette.red
          : theme.palette.neutralPrimary,
    },
    field: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      maxWidth: '500px',
    },
  };
};

export const getCaptionRootStyles = (): IStyle => {
  return {
    backgroundColor: 'transparent',
    whiteSpace: 'normal',
  };
};

export const getCaptionStyles = (): IStyle => {
  return {
    backgroundColor: 'transparent',
    margin: '11px 0px 12px 0px',
  };
};

export const getButtonStyles = (): IStyle => {
  return {
    marginTop: '12px',
  };
};

interface ISubwayNavProgressBarStyles {
  before: IStyle;
  after: IStyle;
}

export const getSubwayProgressBarStyles = (
  props: IThemedProp,
  beforeHeight: string,
  afterHeight: string
): ISubwayNavProgressBarStyles => {
  const { theme } = props;
  return {
    before: {
      content: '',
      display: 'block',
      backgroundColor: theme.palette.neutralTertiary,
      height: beforeHeight || 0,
      width: '2px',
      zIndex: 0,
      position: 'absolute',
      top: '24px',
      left: '7px',
    },
    after: {
      content: '',
      display: 'block',
      height: afterHeight || 0,
      backgroundColor: theme.palette.blue,
      width: '2px',
      zIndex: 1,
      position: 'absolute',
      top: '24px',
      left: '7px',
    },
  };
};

export const getAccessibilityLabelStyles = (): IStyle => {
  return {
    height: '0px',
    width: '0px',
    visibility: 'hidden',
  };
};

export interface ISubwayNavStepsStyles {
  captionRoot: IStyle;
  caption: IStyle;
  button: IStyle;
  hiddenLabel?: IStyle;
}

export interface IRequestSectionStyles {
  sectionContainer: IStyle;
  title: IStyle;
  contentContainer: IStyle;
  contentmain: IStyle;
  contentsecondary: IStyle;
  endComponentMargin: IStyle;
  textRowContainer: IStyle;
  textTruncate: IStyle;
  attachmentsContent: IStyle;
  flexContainer: IStyle;
  attachmentsInfo: IStyle;
  scrollableContent: IStyle;
  attachmentsToolTip: IStyle;
  requestSteps?: IStyle;
}

export interface IFieldStyles {
  fieldSection: IStyle;
  fieldLabel: IStyle;
  fieldLabelRequired: IStyle;
  fieldFlexLine: IStyle;
  dialingCodeDropdown: IStyle;
  fieldInputGroup: IStyle;
  phoneNumberField: IStyle;
  fieldInput: IStyle;
  fieldInputInvalidMessage: IStyle;
  fieldInputDisabled: IStyle;
  notesDisplayContainer: IStyle;
  noteContainer: IStyle;
  noteName: IStyle;
  noteTime: IStyle;
  dropDown: Partial<IComboBoxStyles>;
  phoneField: Partial<ITextFieldStyles>;
  iconButton: Partial<IButtonStyles>;
  comboBoxOption: Partial<IComboBoxOptionStyles>;
  severityContainer: IStyle;
  serveritySubContainer: IStyle;
  severityLabel: IStyle;
  severityText: IStyle;
  serverityMessageBar: IStyle;
  severityABCDropdown: IStyle;
  severityRequiredLabel: IStyle;
  severityCheckBox: Partial<ICheckboxStyles>;
  severityDropdown: Partial<IDropdownStyles>;
}

export const getGetHelpTextFieldStyles = (theme: ITheme): Partial<ITextFieldStyles> => ({
  root: {
    borderRadius: '4px',
    borderBottomColor: theme.palette.neutralSecondaryAlt,
    flexGrow: 1,
  },
  field: {
    borderBottomColor: theme.palette.neutralSecondaryAlt,
    selectors: {
      '&:hover': {
        borderBottomColor: theme.palette.neutralSecondaryAlt,
        textDecorationColor: theme.palette.neutralSecondary,
        backgroundColor: theme.semanticColors.bodyStandoutBackground,
      },
      '&:focus': {
        borderBottomColor: theme.palette.themeDarkAlt,
      },
      '&:rest:': {
        borderBottomColor: theme.palette.neutralSecondaryAlt,
      },
      '&:input:': {
        borderBottomColor: theme.palette.neutralSecondaryAlt,
      },
    },
  },
});

export const getFieldStyles = (props?: IRequestDetailsThemedProps): IFieldStyles => {
  const { theme, sourceApp, isDarkTheme, isNonAgave, isTeams } = props;
  const isGetHelp = isGetHelpSourceApp(sourceApp);

  const baseStyles: IFieldStyles = {
    fieldInputDisabled: {
      backgroundColor: isDarkTheme ? '#444444 !important' : '#eee !important',
      color: `${theme.palette.neutralPrimary} !important`,
      border: `1px solid #ccc !important`,
      cursor: 'not-allowed !important',
      transition: 'border-color ease-in-out .15s,box-shadow ease-in-out .15s',
      opacity: 1,
    },
    comboBoxOption: isGetHelp
      ? undefined
      : {
          root: {
            backgroundColor: theme.semanticColors.inputBackground,
          },
          rootCheckedHovered: {
            backgroundColor: color.gray,
          },
          rootHovered: {
            backgroundColor: color.gray,
          },
        },
    phoneField: isGetHelp
      ? getGetHelpTextFieldStyles(theme)
      : {
          root: {
            borderRadius: '2px',
            border: 'none !important',
            height: '32px',
            background: theme.semanticColors.inputBackground,
            color: theme.palette.neutralPrimary,
          },
          fieldGroup: {
            background: theme.semanticColors.inputBackground,
          },
          field: {
            color: theme ? theme.palette.neutralPrimary : 'black',
            border: 'none !important',
          },
        },
    dropDown: isGetHelp
      ? {
          root: {
            display: 'flex',
            padding: '0px',
            selectors: {
              '::after': {
                borderRadius: '4px',
                borderColor: theme.palette.neutralLight,
              },
            },
          },
          container: {
            marginTop: '4px',
            marginRight: '15px',
          },
          input: {
            height: '100%',
            paddingLeft: '8px',
            selectors: {
              '::placeholder': {
                paddingBottom: '2px',
              },
              ':-ms-input-placeholder': {
                paddingBottom: '2px',
              },
            },
          },
        }
      : {
          root: {
            display: 'flex',
            padding: 0,
            paddingLeft: '8px',
            backgroundColor: theme.semanticColors.inputBackground,
            boxSizing: 'border-box',
            borderRadius: '2px',
            boxShadow: isTeams ? '0 2px 4px rgb(0 0 0 / 10%)' : 'none',
          },
          rootHovered: {
            border: `1px solid ${theme.semanticColors.inputBackground}`
          },
          container: {
            marginTop: '4px',
            marginRight: '10px',
            borderRadius: '2px',
            flex: 1,
            background: theme.semanticColors.inputBackground,
          },
          input: {
            borderStyle: 'none !important',
            backgroundColor: theme.semanticColors.inputBackground,
            height: '100%',
            flex: '7 1 0',
            selectors: {
              '::placeholder': {
                paddingBottom: '2px',
              },
              ':-ms-input-placeholder': {
                paddingBottom: '2px',
              },
              ':hover': {
                backgroundColor: theme.semanticColors.inputBackground,
              },
            },
          },
          rootFocused: {
            border: `2px solid ${theme.palette.themeTertiary}`,
            selectors: {
              '::after': {
                borderColor: `${theme.semanticColors.inputBackground} !important`,
              },
              ':-ms-input-placeholder': {
                paddingBottom: '2px',
              },
            },
          },
          optionsContainer: {
            backgroundColor: theme.semanticColors.inputBackground,
          },
        },
    iconButton: isGetHelp
      ? {
          root: {
            borderRadius: '4px',
          },
          icon: {
            color: theme.palette.neutralPrimary,
          },
        }
      : {
          root: {
            marginTop: '0px !important',
            borderRadius: '2px',
            flex: '1 1 0',
            background: `${theme.palette.themeTertiary} !important`,
            transition: 'background-color 180ms linear',
            height: '100%'
          },
          rootHovered: {},
          rootChecked: {
            background: `${theme.palette.themeTertiary} !important`,
          },
          rootExpanded: {
            background: `${theme.palette.themeTertiary} !important`,
          },
          icon: {
            ...(isNonAgave && { marginLeft: '0px', marginRight: '0px' }),
            color: sourceApp === SourceApps.SurfaceApp || sourceApp === SourceApps.SurfaceApp2 ? theme.palette.neutralPrimary : theme.semanticColors.primaryButtonText,
          },
          iconChecked: {
            color: theme.semanticColors.primaryButtonText,
          },
          iconHovered: {
            color: theme.semanticColors.primaryButtonText,
          },
          iconExpandedHovered: {
            color: theme.semanticColors.primaryButtonText,
          },
        },
    fieldSection: {
      marginBottom: '10px',
    },
    fieldLabel: {
      fontWeight: 500,
    },
    fieldLabelRequired: {
      color: theme.palette.red,
    },
    fieldFlexLine: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    dialingCodeDropdown: {
      margin: '4px 10px 0 0',
      flex: 1,
    },
    fieldInputGroup: {
      marginTop: '4px',
      flex: 2,
    },
    phoneNumberField: {
      marginTop: '4px',
    },
    fieldInput: {
      border: '1px solid #ccc',
      background: `${theme.semanticColors.inputBackground} !important`,
      color: `${theme.palette.neutralPrimary} !important`,
    },
    fieldInputInvalidMessage: {
      fontSize: '12px',
      color: theme.palette.red,
    },
    notesDisplayContainer: {
      marginTop: '5px',
      marginBottom: '5px',
      maxWidth: '100%',
    },
    noteContainer: {
      display: 'flex',
      maxWidth: '90%',
      overflow: 'hidden',
    },
    noteName: {
      fontSize: '14px',
      justifyContent: 'flex-start',
      marginLeft: '5px',
      color: theme.palette.neutralPrimary,
    },
    noteTime: {
      fontSize: '14px',
      justifyContent: 'flex',
      color: theme.palette.neutralPrimary,
    },
    severityContainer: {
      marginBottom: '10px',
    },
    serveritySubContainer: {
      marginTop: '5px',
    },
    severityLabel: {
      fontWeight: '500 !important',
      marginBottom: '3px',
      fontSize: '14px',
    },
    severityText: {
      whiteSpace: 'initial',
    },
    serverityMessageBar: {
      whiteSpace: 'initial',
      marginTop: '5px',
    },
    severityABCDropdown: {
      marginBottom: '5px',
      width: '250px',
    },
    severityRequiredLabel: {
      color: theme.palette.red,
    },
    severityCheckBox: {
      checkbox: {
        selectors: {
          ':hover': {
            backgroundColor: theme.palette.themePrimary,
          },
        },
      },
      checkmark: {
        selectors: {
          ':hover': {
            backgroundColor: theme.palette.themePrimary,
          },
        },
      },
    },
    severityDropdown: {
      root: {
        width: '100%',
        marginBottom: '5px',
        backgroundColor: theme.semanticColors.inputBackground,
        selectors: {
          ':hover': {
            borderColor: theme.palette.themePrimary,
          },
        },
      },
      dropdownOptionText: {
        overflow: 'visible' as const,
        whiteSpace: 'normal',
      },
      dropdownItem: {
        height: 'auto',
        padding: '8px',
        minHeight: '40px',
      },
      dropdownItemSelected: {
        height: 'auto',
        padding: '8px',
        minHeight: '40px',
      },
      dropdownItemHeader: {
        fontSize: '12px',
      },
      callout: {
        marginRight: '2px',
      },
    },
  };
  if (
    sourceApp === SourceApps.Teams ||
    sourceApp === SourceApps.Teams2 ||
    sourceApp === SourceApps.TeamsAdmin ||
    sourceApp === SourceApps.TeamsAdmin2
  ) {
    baseStyles.fieldInput['border'] = '1px solid #ccc !important';
    baseStyles.fieldInput['borderRadius'] = '4px !important';
  }
  return baseStyles;
};

export const getTextFieldStyles = (props: IThemedProp & ITextFieldStylesProps): Partial<ITextFieldStyles> => {
  const { theme, isInputValid, isPhoneOrEmailField, isTeams } = props;
  return {
    subComponentStyles: {
      // determines the style of the required asterisk
      label: {
        root: {
          selectors: {
            '::after': {
              color: theme.palette.red,
            },
          },
        },
      },
    },
    fieldGroup: {
      border: isPhoneOrEmailField && isInputValid ? `1px solid ${theme.semanticColors.inputBorder}` : '1px solid #ccc !important',
      boxShadow: isTeams ? '0 2px 4px rgb(0 0 0 / 10%)' : 'none',
      selectors: {
        ':hover': {
          border: '1px solid #ccc',
        },
        '::after': {
          borderColor: !isInputValid && theme.palette.themePrimary,
        },
      },
    },
    errorMessage: {
      color: theme.palette.red,
    },
  };
};

export const getRequestSectionStyles = (props?: IThemedProp, contentSecondaryWidth?: string): IRequestSectionStyles => {
  const sectionContainer: { [key: string]: string } = {
    marginBottom: '32px',
    // display: 'flex',
    fontSize: '14px',
  };
  const contentContainer: { [key: string]: string } = {
    marginBottom: '16px',
    display: contentSecondaryWidth ? 'flex' : 'block',
    alignItems: contentSecondaryWidth ? 'center' : 'inherit',
    // justifyContent: contentSecondaryWidth ? 'space-between' : 'unset',
    flexWrap: 'nowrap',
    width: '100%',
  };
  const baseStyles: IRequestSectionStyles = {
    sectionContainer: {},
    contentContainer: {},
    title: {
      // marginBottom: '16px',
      fontWeight: '600',
    },
    contentmain: {
      flexGrow: 1,
    },
    contentsecondary: {
      width: contentSecondaryWidth ? contentSecondaryWidth : '0',
    },
    endComponentMargin: {
      marginBottom: '48px',
    },
    textRowContainer: {
      // To display emails on stack of other
      display: 'flex',
      flexDirection: 'column',
      wordWrap: 'break-word',
    },
    textTruncate: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    attachmentsContent: {
      width: '80%',
    },
    flexContainer: {
      display: 'flex',
    },
    attachmentsInfo: {
      alignItems: 'center',
    },
    scrollableContent: {
      overflowY: 'auto',
      maxHeight: '185px',
      padding: '2px 2px 2px 2px',
    },
    attachmentsToolTip: {
      fontSize: '17px',
      paddingLeft: '7px',
      paddingTop: '2px',
    },
  };
  if (!!props) {
    const { theme } = props;
    baseStyles.requestSteps = {
      backgroundColor: 'transparent !important',
      color: `${theme.palette.neutralPrimary} !important`,
      selectors: {
        svg: {
          backgroundColor: 'transparent !important',
          width: '16px',
        },
        'svg> text': {
          backgroundColor: theme.palette.themeTertiary,
          fill: `${theme.palette.blue} !important`,
        },
      },
    };
  }
  baseStyles.sectionContainer = sectionContainer;
  baseStyles.contentContainer = contentContainer;
  return baseStyles;
};

export const getSubwayNavSpinnerStyles = (): Partial<ISpinnerStyles> => {
  return {
    root: {
      maxWidth: '80px',
    },
  };
};

export const getSubwayNavStyles = (props: IThemedProp, isIE: boolean): Partial<ISubwayNavStyles> => {
  const { theme } = props;
  return {
    root: {
      '::after': !isIE
        ? {
            fontSize: '0',
            backgroundColor: theme.palette.themeTertiary,
          }
        : {
            content: 'none',
          },
      '::before': !isIE
        ? {}
        : {
            content: 'none',
          },
    },
  };
};

export const getChatButtonStyles = (props: IThemedProp): Partial<IButtonStyles> => {
  const { theme } = props;
  return {
    root: {
      backgroundColor: theme.palette.themeTertiary,
    },
    rootHovered: {
      backgroundColor: theme.palette.themeTertiary,
    },
  };
};

export const getSubwayNavStepStyles = (_: IThemedProp): ISubwayNavStepsStyles => {
  return {
    captionRoot: getCaptionRootStyles(),
    caption: getCaptionStyles(),
    button: getButtonStyles(),
    hiddenLabel: getAccessibilityLabelStyles(),
  };
};

export const getSubwayNavClassNames = classNamesFunction<{}, ISubwayNavStepsStyles>();

export const getRequestDetailsClassNames = classNamesFunction<{}, IRequestDetailsStyles>();
export const getRequestSectionClassNames = classNamesFunction<{}, IRequestSectionStyles>();
export const getFieldClassNames = classNamesFunction<{}, IFieldStyles>();
